import React, { useContext, useEffect, useRef, useState } from "react";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import AppSettings from "../../../Settings/AppSettings";
import { AppContext } from "../../../Context/AppContext";
import { useNavigate } from "react-router";
import { toast } from "Components/DesignSystem/Toast/useToast";
import Button from "Components/DesignSystem/Button/Button";
import Divider from "Components/DesignSystem/Divider/Divider";
import Typography from "Components/DesignSystem/Typography/Typography";

/**
 * `/admin/settings` — four maintenance triggers behind `gmssettings_edit`.
 *
 * On the design system since `okf/design-system/e2e.md` § 134. Every endpoint,
 * argument, toast string and lock duration is byte-identical to the legacy screen;
 * what changed is the markup (Bootstrap `d-flex`/`border-bottom`/`fs-5 fw-bolder`
 * `<label>`s → `Typography` + `Divider`), the button (`GMSFillButton` → DS `Button`),
 * and the fact that the timers below are now cancelled on unmount.
 *
 * **Score Calculation is disabled on purpose, and must stay that way.** Its backend
 * route is fully commented out — `App_Flask/routers.py:1269–1274`, documented in
 * `ProductCatalogue-Backend/Docs/api/admin-settings/admin-settings.md`. Enabling the
 * button would fire `GET /refresh/score_calculation` at a 404. That doc also records a
 * decorator-order landmine in the dead route (no `@jwt_required`), so reviving this
 * needs a backend change first, not a one-character edit here.
 */

/**
 * `ABS` and `CF` are deliberately NOT expanded in the copy below.
 *
 * They are opaque flags carried end-to-end as `abs_*` / `cf_*` property names, and the
 * backend module doc says so explicitly: "ABS and CF are not expanded anywhere in code
 * or docs… treat [Attribute-Based Selection] as the likely origin of 'ABS', not a
 * confirmed acronym. No equivalent clue exists for 'CF'." A helpful-looking expansion
 * here would be the first place in either repo to assert one.
 */
const LOCK_7_MINUTES = 420000;
const LOCK_1_MINUTE = 60000;

const SettingsView = () => {
  const [disableCacheButton, setDisableCacheButton] = useState(false);
  const [disableABSButton, setDisableABSButton] = useState(false);
  /*
    Only the SETTER is read. `triggerScorefresh` / `checkScoreStatus` still maintain
    this state, but the Score button below is disabled unconditionally (see the file
    docblock), so nothing consumes the value. Kept as state rather than deleted so that
    re-enabling the button once the backend route exists is a one-line change here,
    not a re-derivation of the whole flow.
  */
  const [, setDisableScoreButton] = useState(false);
  const [disableCFButton, setDisableCFButton] = useState(false);
  /*
    A plain `let`, not a ref, and that is load-bearing.

    `checkScoreStatus` recurses into ITSELF via setTimeout, so every hop in one polling
    chain shares this one closure — which is exactly the dedupe scope wanted: "don't
    re-announce a status this run has already announced". A `useRef` would persist
    across separate runs and swallow the first status of the second one. Unreachable
    today (the Score button never enables), so this is preserved as-is rather than
    modernised into a different behaviour.
  */
  let scoreStatus = '';
  const [loaded, setloaded] = useState(false);
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const appData = useContext(AppContext);
  const navigate = useNavigate();

  /*
    Every setTimeout this screen starts, so unmount can cancel it.

    The locks run for up to 7 minutes and the score poll re-arms itself every 10s
    forever while a job reports `processing` — navigating away left both running and
    calling setState on an unmounted tree. Nothing observable changes while mounted:
    the ids are only ever cleared by the unmount effect below.
  */
  const timersRef = useRef([]);
  const schedule = (fn, ms) => {
    timersRef.current.push(setTimeout(fn, ms));
  };
  useEffect(() => () => {
    /* `(id) => clearTimeout(id)`, not a bare `clearTimeout` reference: `forEach` calls
       its callback with `(value, index, array)`, so the point-free version invokes
       `clearTimeout(id, 0, [...])`. Harmless at runtime — the extra arguments are
       ignored — but it makes the call unassertable, which is how the guard would rot. */
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (!appData.pm.checkPermission("gmssettings_edit")) {
      navigate("/workspace/module/permission/denied");
    } else {
      setloaded(true);
    }
  }, []); // eslint-disable-line

  /**
   * The shared shape of Cache / ABS / CF: disable, GET, then either lock for a fixed
   * window or re-enable on failure.
   *
   * These were three copies of the same eleven lines differing only in URL, message and
   * lock duration. The success predicate (`!res.errorfound && res?.data`) and the error
   * copy ("Error Occurred") were already identical in all three. Score is NOT routed
   * through here — it checks `res?.data?.done` and hands off to a poller instead.
   */
  const triggerRefresh = async ({ path, message, lockMs, setDisabled }) => {
    setDisabled(true);
    let res = await ServiceWrapper.doGet(
      api_base_url + path,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound && res?.data) {
      toast.info(message);
      schedule(() => {
        setDisabled(false);
      }, lockMs);
    } else {
      toast.error("Error Occurred");
      setDisabled(false);
    }
  };

  const triggerCacheRefresh = () =>
    triggerRefresh({
      path: "masterdata/generate",
      message:
        "Triggered successfully, the button will be locked for next 7 minutes.",
      lockMs: LOCK_7_MINUTES,
      setDisabled: setDisableCacheButton,
    });

  const triggerABSfresh = () =>
    triggerRefresh({
      path: "refresh/abs",
      message: "Triggered successfully, the button will be locked for 1 minute.",
      lockMs: LOCK_1_MINUTE,
      setDisabled: setDisableABSButton,
    });

  const triggerCFRefresh = () =>
    triggerRefresh({
      path: "refresh/cf",
      message: "Triggered successfully, the button will be locked for 1 minute.",
      lockMs: LOCK_1_MINUTE,
      setDisabled: setDisableCFButton,
    });

  async function checkScoreStatus(jobId) {
    if (jobId) {
      let ret = await ServiceWrapper.doGet(
        api_base_url + "job/status/" + jobId,
        null,
        null,
        AuthManager.getGmsToken()
      );

      if (!ret.errorfound) {
        if (ret.data.processing) {
          schedule(() => {
            checkScoreStatus(jobId);
          }, 10000);
          if (scoreStatus !== ret.data.status) {
            scoreStatus = ret.data.status;
            toast.success(ret.data.status);
          }
          return;
        }
        else {
          setDisableScoreButton(false)
        }

        if (ret.data.error) {
          toast.error(ret.data.status);
          return;
        }
      }
    } else {
      toast.error("Job Id not found");
      setDisableScoreButton(false)
    }
  }

  const triggerScorefresh = async () => {
    setDisableScoreButton(true)
    let res = await ServiceWrapper.doGet(
      api_base_url + "refresh/score_calculation",
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound && res?.data && res?.data?.done) {
      let task_id = res.data.task_id
      checkScoreStatus(task_id)
    } else {
      toast.error("Error Occurred");
      setDisableScoreButton(false);
    }
  }

  /*
    The four rows, as data.

    `disabled` is read per render, so these are rebuilt each pass rather than hoisted to
    module scope.
  */
  const rows = [
    {
      id: "cache",
      label: "Cache Management",
      description: "Regenerates master data. Locked for 7 minutes after each trigger.",
      action: "Trigger Cache",
      testId: "admin-settings-trigger-cache-btn",
      disabled: disableCacheButton,
      onClick: triggerCacheRefresh,
    },
    {
      id: "abs",
      label: "ABS Management",
      description:
        "Regenerates ABS data for eligible products in the background. Locked for 1 minute after each trigger.",
      action: "Trigger ABS",
      testId: "admin-settings-trigger-abs-btn",
      disabled: disableABSButton,
      onClick: triggerABSfresh,
    },
    {
      id: "score",
      label: "Score Calculation",
      description: "Currently unavailable.",
      action: "Trigger Score",
      testId: "admin-settings-trigger-score-btn",
      /* Unconditional. The backend route is commented out — see the file docblock. */
      disabled: true,
      onClick: triggerScorefresh,
    },
    {
      id: "cf",
      label: "CF Management",
      description:
        "Regenerates CF data for eligible products in the background. Locked for 1 minute after each trigger.",
      action: "Trigger CF",
      testId: "admin-settings-trigger-cf-btn",
      disabled: disableCFButton,
      onClick: triggerCFRefresh,
    },
  ];

  if (!loaded) return null;

  return (
    <div data-testid="admin-settings-page">
      {/*
        `heading-2` (18px/500) for the page, `title-header` (14px/600) for the sections.

        The legacy screen gave the page title and all four section names the SAME
        `fs-5 fw-bolder` — 20px/700 each — so "Settings" did not out-rank "Cache
        Management" by so much as a pixel and the page had no visual outline at all.
        They were also `<label>` elements with no `htmlFor`, which is a labelling
        element used as a heading: nothing for a screen reader to navigate by.
      */}
      <div className="tw-py-2">
        {/* tw-text-ink is explicit: heading variants are SIZES, not inks, and the
            inherited default is black — invisible in dark (same fix as the List h1s). */}
        <Typography variant="display" as="h1" className="tw-text-ink" data-testid="admin-settings-title">
          Settings
        </Typography>
      </div>
      <Divider />

      {rows.map((row) => (
        <React.Fragment key={row.id}>
          <div
            className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-py-3"
            data-testid={`admin-settings-row-${row.id}`}
          >
            <div className="tw-flex tw-flex-col tw-gap-0.5">
              <Typography
                variant="title-header"
                as="h2"
                className="tw-text-ink"
                data-testid={`admin-settings-row-${row.id}-label`}
              >
                {row.label}
              </Typography>
              <Typography
                variant="sub-body-supporting"
                as="p"
                id={`admin-settings-row-${row.id}-description`}
                className="tw-text-ink-tertiary"
                data-testid={`admin-settings-row-${row.id}-description`}
              >
                {row.description}
              </Typography>
            </div>

            {/*
              `min-w` so the four right-hand buttons form a column instead of four
              ragged left edges — "Trigger Score" is 3 characters wider than "Trigger
              CF", and with `justify-between` that difference lands as a stagger.
            */}
            <Button
              variant="primary"
              className="tw-shrink-0 tw-min-w-[140px]"
              data-testid={row.testId}
              /*
                A `disabled` button is not focusable, so a screen-reader user tabbing
                the page never reaches "Trigger Score" and never learns why it is dead.
                The description is adjacent in reading order, but only this association
                makes it the button's own accessible description rather than a
                paragraph that happens to sit nearby.
              */
              aria-describedby={`admin-settings-row-${row.id}-description`}
              disabled={row.disabled}
              onClick={row.onClick}
            >
              {row.action}
            </Button>
          </div>
          <Divider />
        </React.Fragment>
      ))}
    </div>
  );
};
export default SettingsView;
